// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridItemStyle } from "@insite/mobius/GridItem";
import { StyledTypography } from "@insite/mobius/Link";
import { TextFieldProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductAddToCartButton from "@mcmc/Overrides/Components/ProductAddToCartButton";
import ProductDescription, { ProductDescriptionStyles } from "@mcmc/Overrides/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@mcmc/Overrides/Components/ProductPartNumbers";
import { MultiLineText } from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import { screen } from "@mcmc/XDL/Helpers";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

type Props = Omit<ProductCardSelections, "showImage"> & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductListProductRowStyles {
    productCell?: DataTableCellProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    descriptionStyles?: ProductDescriptionStyles;
    actionsCell?: DataTableCellProps;
    addToCartWrapper?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButton?: ButtonPresentationProps;
    addToList?: ProductAddToListLinkStyles;
}

export const productRowStyles: ProductListProductRowStyles = {
    productCell: {
        css: css`
            & > ${StyledWrapper} {
                width: auto;
                height: auto;
            }
        `,
    },
    partNumbersStyles: {
        container: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                max-height: 55px;
                ${MultiLineText} {
                    width: 220px;
                }
                display: inline-block;
                overflow: hidden;
                line-height: 18px;
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
    actionsCell: {
        alignX: "right",
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                width: 55px;
                margin-right: 10px;
            `,
            formInputWrapper: css`
                width: 55px;
            `,
        },
    },
    addToCartButton: { sizeVariant: "medium" },
    addToList: {
        link: {
            css: css`
                ${getProp("theme.link.defaultProps.css")}
                margin-top: 10px;
            `,
        },
    },
};

const styles = productRowStyles;

const ProductListProductRow: FC<Props> = ({ productContext, productSettings, ...fields }) => {
    const { product } = productContext;

    return (
        <DataTableRow>
            {(fields.showPartNumbers || fields.showCompare) && (
                <DataTableCell className="no-sticky no-wrap" {...styles.productCell}>
                    {fields.showPartNumbers && (
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.partNumbersStyles}
                        />
                    )}
                    <ProductListProductImage showImage={false} showCompare={fields.showCompare} />
                </DataTableCell>
            )}
            {fields.showTitle && (
                <DataTableCell>
                    <ProductDescription
                        product={productContext}
                        includeBrandName={true}
                        extendedStyles={styles.descriptionStyles}
                    />
                    <ProductPartNumbers
                        productNumber=""
                        customerProductNumber=""
                        manufacturerItem=""
                        customerProductReference={product.properties?.referenceNumbers}
                    />
                </DataTableCell>
            )}
            {fields.showAvailability && (
                <DataTableCell>
                    <ProductContextAvailability />
                </DataTableCell>
            )}
            {fields.showPrice && (
                <DataTableCell>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                    />
                </DataTableCell>
            )}
            <DataTableCell {...styles.actionsCell}>
                <StyledWrapper {...styles.addToCartWrapper}>
                    <ProductQuantityOrdered labelOverride="" extendedStyles={styles.productQuantityOrderedStyles} />
                    <ProductAddToCartButton iconOnly extendedStyles={styles.addToCartButton} />
                </StyledWrapper>
                {fields.showAddToList && (
                    <ProductAddToListLink
                        extendedStyles={styles.addToList}
                        data-test-selector={`actionsAddToList${productContext.product.id}`}
                    />
                )}
            </DataTableCell>
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductRow));
